import {
  Avatar,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import { DatePickerInput } from 'Components/HookFormInputs/DatePickerInput';
import { TextInput } from 'Components/HookFormInputs/TextInput';
import { defaultMinDate } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useCannabisForm } from 'Hooks/useCannabisForm';
import { PurpleX } from 'Images';
import { useContext, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Buttons } from './Buttons';
import { SelectedInvoiceFile } from '../SelectedInvoiceFile';
import { Subtitle } from './Subtitle';
import { PriceInput } from 'Components/HookFormInputs/PriceInput';
import {
  CannabisContext,
  IInvoice,
  blankInvoice,
} from 'Context/CannabisContext';
import { WarningMessageWithIcon } from 'Components/WarningMessageWithIcon';
import { OcrLoading } from '../OcrLoading';
import { filterAndLimitInput } from 'Utils/reimbursements';

interface IForm {
  invoice: IInvoice;
}

export const maxInvoiceLength = 15;

export const Form = ({ invoice }: IForm) => {
  const {
    handleAddInvoice,
    handleCloseModal,
    watchFile,
    checkIfInvoiceExists,
    editedInvoiceIndex,
    onReturnFromEdit,
    isOcrLoading,
    onExit,
  } = useContext(CannabisContext);
  const {
    CannabisForm,
    CannabisForm: {
      register,
      formState,
      watch,
      handleSubmit,
      reset,
      resetField,
    },
  } = useCannabisForm(checkIfInvoiceExists, invoice);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements).form;
  const isDesktop = useIsDesktop();

  const onSubmit = async () => {
    await handleSubmit((data) => {
      handleAddInvoice({
        ...invoice,
        invoiceTotal: data.invoiceTotal,
        invoiceDate: data.invoiceDate,
        invoiceId: data.invoiceId,
      });
      reset();
      handleCloseModal();
    })();
  };

  const resetAndExit = () => {
    reset();
    handleCloseModal();
  };

  const getEndAdornment = (inputName: 'invoiceId' | 'invoiceTotal') =>
    !!watch(inputName) ? (
      <InputAdornment position='end'>
        <IconButton
          onClick={() =>
            resetField(inputName, { defaultValue: blankInvoice[inputName] })
          }
          sx={{ width: '20px', height: '20px' }}
        >
          <Avatar
            alt={Dictionary[DICTIONARY.General].reset}
            src={PurpleX}
            sx={{ width: '20px', height: '20px' }}
          />
        </IconButton>
      </InputAdornment>
    ) : undefined;

  const isNoDataError = useMemo(
    () =>
      editedInvoiceIndex === null &&
      invoice.invoiceId === '' &&
      invoice.invoiceDate === null &&
      invoice.invoiceTotal === null,
    [invoice.invoiceTotal, invoice.invoiceDate, invoice.invoiceId]
  );

  const checkIfShowCancelEditModal = () =>
    editedInvoiceIndex !== null &&
    (invoice.invoiceId !== watch('invoiceId') ||
      invoice.invoiceDate?.toString() !== watch('invoiceDate')?.toString() ||
      invoice.invoiceTotal !== watch('invoiceTotal'));

  const getInvoiceNumberErrors = () =>
    !!(formState.errors as any)?.invoiceId?.message ||
    (!isNoDataError && invoice.invoiceId === '' && !watch('invoiceId'))
      ? {
          message: (
            <WarningMessageWithIcon
              message={
                (formState.errors as any)?.invoiceId?.message ||
                dictionary.missingDataError
              }
              id='invoiceId-error-text'
            />
          ),
        }
      : undefined;

  const getPriceInputErrors = () =>
    !!(formState.errors as any)?.invoiceTotal ||
    (!isNoDataError && invoice.invoiceTotal === null && !watch('invoiceTotal'))
      ? {
          message: (
            <WarningMessageWithIcon
              message={
                (formState.errors as any)?.invoiceTotal?.message ||
                dictionary.missingDataError
              }
              id='price-error-text'
            />
          ),
        }
      : undefined;

  return isOcrLoading ? (
    <OcrLoading />
  ) : (
    <FormProvider {...CannabisForm}>
      <Stack
        sx={{
          alignItems: 'center',
          p: isDesktop ? '80px 0px 48px 140px' : '100px 24px 48px',
          width: isDesktop ? '940px' : '100%',
          gap: isDesktop ? '40px' : '12px',
        }}
      >
        <Typography component={'h1'} variant='h3'>
          {editedInvoiceIndex !== null
            ? dictionary.editTitle
            : dictionary.title}
        </Typography>
        <Stack sx={{ gap: isDesktop ? '48px' : '32px' }}>
          <Subtitle
            isEdit={editedInvoiceIndex !== null}
            isNoDataError={isNoDataError}
          />
          <Stack
            sx={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              width: '100%',
              gap: isDesktop ? '48px' : '20px',
              mt: isDesktop ? '-16px' : undefined,
              '& .MuiOutlinedInput-input': { textAlign: 'left' },
            }}
          >
            <TextInput
              label={dictionary.invoiceNumber}
              register={register('invoiceId', {
                onChange: (e) => {
                  const filteredValue = filterAndLimitInput(
                    e,
                    maxInvoiceLength
                  );
                  e.target.value = filteredValue;
                },
              })}
              shrink={!!watch('invoiceId')}
              aria-labelledby={
                !!(formState.errors as any)?.invoiceId?.message
                  ? 'invoiceId-error-text'
                  : undefined
              }
              errors={getInvoiceNumberErrors()}
              sx={{
                maxWidth: '376px',
                mt: isDesktop ? '4px' : undefined,
                mb: '-6px',
              }}
              endAdornment={getEndAdornment('invoiceId')}
            />
            <DatePickerInput
              label={dictionary.fromDate}
              registerName='invoiceDate'
              minDate={defaultMinDate}
              maxDate={new Date()}
              maxWidth={isDesktop ? '376px' : '100%'}
              minWidth={isDesktop ? '376px' : '100%'}
              errorMessage={
                !isNoDataError &&
                invoice.invoiceDate === null &&
                !watch('invoiceDate')
                  ? dictionary.missingDataError
                  : undefined
              }
            />
            <PriceInput
              register={register('invoiceTotal')}
              aria-labelledby={
                !!(formState.errors as any)?.invoiceTotal
                  ? 'price-error-text'
                  : undefined
              }
              errors={getPriceInputErrors()}
              label={dictionary.amountPaid}
              shrink={!!watch('invoiceTotal')}
              sx={{ width: isDesktop ? '376px' : '100%' }}
              value={watch('invoiceTotal')}
              helperText={
                !isNoDataError &&
                invoice.invoiceTotal === null &&
                !watch('invoiceTotal')
                  ? dictionary.missingDataError
                  : undefined
              }
            />
          </Stack>
          <SelectedInvoiceFile file={invoice.file} watchFile={watchFile} />
          <Buttons
            isEdit={editedInvoiceIndex !== null}
            resetAndExit={() =>
              checkIfShowCancelEditModal()
                ? onReturnFromEdit(onSubmit)
                : onExit(() => resetAndExit())
            }
            onSubmit={onSubmit}
          />
        </Stack>
      </Stack>
    </FormProvider>
  );
};
